// Encrypted Diary Vault for SparkleWorld
// Uses WebCrypto API for secure local encryption

class DiaryVault {
    constructor() {
        this.sessionDEK = null; // Data Encryption Key (in-memory during session)
        this.isUnlocked = false;
    }

    /**
     * Derive Key Encryption Key from user password
     * @param {string} emojiPassword - User's emoji password
     * @param {Uint8Array} salt - Random salt (16 bytes)
     * @returns {Promise<CryptoKey>} KEK for wrapping/unwrapping DEK
     */
    async deriveKEK(emojiPassword, salt) {
        const encoder = new TextEncoder();
        const keyMaterial = await crypto.subtle.importKey(
            'raw',
            encoder.encode(emojiPassword),
            { name: 'PBKDF2' },
            false,
            ['deriveBits', 'deriveKey']
        );

        return crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                salt: salt,
                iterations: 100000,
                hash: 'SHA-256'
            },
            keyMaterial,
            { name: 'AES-GCM', length: 256 },
            false,
            ['wrapKey', 'unwrapKey']
        );
    }

    /**
     * Initialize a new vault (first-time setup)
     * @param {string} emojiPassword - User's chosen password
     * @param {object} db - Dexie database instance
     * @returns {Promise<void>}
     */
    async initializeVault(emojiPassword, db) {
        // Check if vault already exists
        const existing = await db.vaultMeta.get('diary_vault');
        if (existing) {
            throw new Error('Vault already exists. Use unlockVault instead.');
        }

        // Generate random vault salt
        const vaultSalt = crypto.getRandomValues(new Uint8Array(16));

        // Derive KEK from password
        const kek = await this.deriveKEK(emojiPassword, vaultSalt);

        // Generate random DEK
        const dek = await crypto.subtle.generateKey(
            { name: 'AES-GCM', length: 256 },
            true,
            ['encrypt', 'decrypt']
        );

        // Wrap DEK with KEK
        const wrapIV = crypto.getRandomValues(new Uint8Array(12));
        const wrappedDEK = await crypto.subtle.wrapKey(
            'raw',
            dek,
            kek,
            { name: 'AES-GCM', iv: wrapIV }
        );

        // Store vault metadata in IndexedDB
        await db.vaultMeta.put({
            id: 'diary_vault',
            vaultSalt: Array.from(vaultSalt),
            wrappedDEK: Array.from(new Uint8Array(wrappedDEK)),
            wrapIV: Array.from(wrapIV),
            createdAt: new Date().toISOString()
        });

        // Store DEK in session
        this.sessionDEK = dek;
        this.isUnlocked = true;
    }

    /**
     * Unlock existing vault with password
     * @param {string} emojiPassword - User's password
     * @param {object} db - Dexie database instance
     * @returns {Promise<void>}
     */
    async unlockVault(emojiPassword, db) {
        // Load vault metadata
        const meta = await db.vaultMeta.get('diary_vault');
        if (!meta) {
            throw new Error('No vault found. Please create a vault first.');
        }

        // Derive KEK from password
        const kek = await this.deriveKEK(
            emojiPassword,
            new Uint8Array(meta.vaultSalt)
        );

        // Try to unwrap DEK (will fail if wrong password)
        try {
            const dek = await crypto.subtle.unwrapKey(
                'raw',
                new Uint8Array(meta.wrappedDEK),
                kek,
                { name: 'AES-GCM', iv: new Uint8Array(meta.wrapIV) },
                { name: 'AES-GCM', length: 256 },
                false,
                ['encrypt', 'decrypt']
            );

            this.sessionDEK = dek;
            this.isUnlocked = true;
        } catch {
            throw new Error('Wrong password');
        }
    }

    /**
     * Lock the vault (clear session key)
     */
    lockVault() {
        this.sessionDEK = null;
        this.isUnlocked = false;
    }

    /**
     * Encrypt a diary entry
     * @param {string} plaintext - Entry content
     * @returns {Promise<object>} { ciphertext, iv }
     */
    async encryptEntry(plaintext) {
        if (!this.isUnlocked || !this.sessionDEK) {
            throw new Error('Vault is locked. Please unlock first.');
        }

        const iv = crypto.getRandomValues(new Uint8Array(12));
        const encoder = new TextEncoder();

        const encrypted = await crypto.subtle.encrypt(
            { name: 'AES-GCM', iv },
            this.sessionDEK,
            encoder.encode(plaintext)
        );

        return {
            ciphertext: Array.from(new Uint8Array(encrypted)),
            iv: Array.from(iv)
        };
    }

    /**
     * Decrypt a diary entry
     * @param {Array} ciphertext - Encrypted content
     * @param {Array} iv - Initialization vector
     * @returns {Promise<string>} Decrypted plaintext
     */
    async decryptEntry(ciphertext, iv) {
        if (!this.isUnlocked || !this.sessionDEK) {
            throw new Error('Vault is locked. Please unlock first.');
        }

        const decoder = new TextDecoder();

        const decrypted = await crypto.subtle.decrypt(
            { name: 'AES-GCM', iv: new Uint8Array(iv) },
            this.sessionDEK,
            new Uint8Array(ciphertext)
        );

        return decoder.decode(decrypted);
    }

    /**
     * Check if a vault exists
     * @param {object} db - Dexie database instance
     * @returns {Promise<boolean>}
     */
    async vaultExists(db) {
        const meta = await db.vaultMeta.get('diary_vault');
        return !!meta;
    }

    /**
     * Erase the entire vault (irreversible)
     * @param {object} db - Dexie database instance
     * @returns {Promise<void>}
     */
    async eraseVault(db) {
        // Clear all diary entries
        await db.diary.clear();

        // Delete vault metadata
        await db.vaultMeta.delete('diary_vault');

        // Clear session
        this.lockVault();
    }

    /**
     * Export encrypted backup to JSON file
     * @param {object} db - Dexie database instance
     * @returns {Promise<void>}
     */
    async exportVault(db) {
        const meta = await db.vaultMeta.get('diary_vault');
        const entries = await db.diary.toArray();

        if (!meta) {
            throw new Error('No vault to export');
        }

        const backup = {
            version: 1,
            exportedAt: new Date().toISOString(),
            vaultMeta: meta,
            entries: entries
        };

        // Create downloadable JSON file
        const blob = new Blob([JSON.stringify(backup, null, 2)], {
            type: 'application/json'
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sparkleworld-diary-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    /**
     * Import encrypted backup from JSON file
     * @param {File} file - Backup file
     * @param {object} db - Dexie database instance
     * @returns {Promise<void>}
     */
    async importVault(file, db) {
        const text = await file.text();
        const backup = JSON.parse(text);

        // Validate backup format
        if (backup.version !== 1) {
            throw new Error('Unsupported backup version');
        }

        if (!backup.vaultMeta || !backup.entries) {
            throw new Error('Invalid backup file format');
        }

        // Restore vault metadata
        await db.vaultMeta.put(backup.vaultMeta);

        // Restore entries
        await db.diary.clear();
        if (backup.entries.length > 0) {
            await db.diary.bulkAdd(backup.entries);
        }

        // Lock vault (user needs to unlock with password)
        this.lockVault();
    }
}

// Create singleton instance
export const vault = new DiaryVault();
